/**
 * WordCloudWidget - Composant de nuage de mots utilisant d3-cloud
 *
 * Affiche les mots avec une taille proportionnelle à leur fréquence.
 * Utilise les couleurs du thème workspace pour une intégration visuelle cohérente.
 */

import React, { useEffect, useRef, useMemo, useState } from "react";
import cloud from "d3-cloud";
import { select } from "d3-selection";
import { scaleLinear } from "d3-scale";
import { Typography, Loading } from "@/components/ui";
import { useChartColors } from "@/hooks/use-chart-colors";
import { useWorkspaceTheme } from "@/hooks/use-workspace-theme";
import type { ChartData, WordCloudVisualConfig } from "@/models/widget-v2";

// ============================================================================
// Types
// ============================================================================

interface WordCloudWidgetProps {
  data: ChartData;
  visual: WordCloudVisualConfig;
}

interface Word {
  text: string;
  size: number;
  value: number;
  color: string;
  x?: number;
  y?: number;
  rotate?: number;
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_MIN_FONT_SIZE = 12;
const DEFAULT_MAX_FONT_SIZE = 64;
const DEFAULT_MAX_WORDS = 80;
const PADDING = 4;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Transforme les données ChartData en format pour d3-cloud
 */
function transformData(
  data: ChartData,
  colors: string[],
  maxWords: number,
  minFontSize: number,
  maxFontSize: number
): Word[] {
  const labels = data.labels || [];
  const values = data.datasets[0]?.data || [];

  // Créer les paires mot/valeur et trier par valeur décroissante
  const words = labels
    .map((label, index) => ({
      text: label || "",
      value: typeof values[index] === "number" ? values[index] : 0,
    }))
    .filter((w) => w.text && w.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, maxWords);

  if (words.length === 0) return [];

  // Calculer l'échelle de taille
  const minValue = Math.min(...words.map((w) => w.value));
  const maxValue = Math.max(...words.map((w) => w.value));

  const sizeScale = scaleLinear()
    .domain([minValue, maxValue])
    .range([minFontSize, maxFontSize]);

  // Assigner taille et couleur
  return words.map((word, index) => ({
    text: word.text,
    value: word.value,
    size: sizeScale(word.value),
    color: colors[index % colors.length],
  }));
}

// ============================================================================
// Empty State
// ============================================================================

const EmptyState: React.FC = () => (
  <div className="flex h-full w-full flex-col items-center justify-center text-content-tertiary">
    <Typography variant="text-sm">Aucune donnée à afficher</Typography>
  </div>
);

// ============================================================================
// Main Component
// ============================================================================

export const WordCloudWidget: React.FC<WordCloudWidgetProps> = ({
  data,
  visual,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  // Récupérer les couleurs du thème
  const themeColors = useChartColors();
  const { isNight } = useWorkspaceTheme();

  // Extraire les options visuelles avec valeurs par défaut
  const maxWords = visual.maxWords ?? DEFAULT_MAX_WORDS;
  const minFontSize = visual.minFontSize ?? DEFAULT_MIN_FONT_SIZE;
  const maxFontSize = visual.maxFontSize ?? DEFAULT_MAX_FONT_SIZE;
  const rotationEnabled = visual.rotationEnabled ?? true;

  // Couleurs à utiliser (custom ou thème)
  const colors = useMemo(() => {
    if (visual.colors?.length) {
      return visual.colors;
    }
    return themeColors.vibrant;
  }, [visual.colors, themeColors.vibrant]);

  // Transformer les données
  const words = useMemo(() => {
    return transformData(data, colors, maxWords, minFontSize, maxFontSize);
  }, [data, colors, maxWords, minFontSize, maxFontSize]);

  // Observer les changements de taille du conteneur
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        const { width, height } = entry.contentRect;
        setDimensions({ width, height });
      }
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, []);

  // Générer le nuage de mots
  useEffect(() => {
    if (
      !svgRef.current ||
      words.length === 0 ||
      dimensions.width === 0 ||
      dimensions.height === 0
    ) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const svg = select(svgRef.current);
    svg.selectAll("*").remove();

    const { width, height } = dimensions;

    // Créer le layout d3-cloud
    const layout = cloud<Word>()
      .size([width, height])
      .words(words.map((w) => ({ ...w })))
      .padding(PADDING)
      .rotate(() => {
        if (!rotationEnabled) return 0;
        // Rotation aléatoire: 0°, 90°, ou -90° avec probabilités différentes
        const rand = Math.random();
        if (rand < 0.65) return 0;
        if (rand < 0.85) return 90;
        return -90;
      })
      .font("system-ui, -apple-system, sans-serif")
      .fontSize((d) => d.size || minFontSize)
      .spiral("archimedean")
      .on("end", (computedWords) => {
        // Créer le groupe centré
        const g = svg
          .append("g")
          .attr("transform", `translate(${width / 2},${height / 2})`);

        // Ajouter les mots avec animation CSS
        const textElements = g
          .selectAll("text")
          .data(computedWords)
          .enter()
          .append("text")
          .style("font-size", (d) => `${d.size}px`)
          .style("font-family", "system-ui, -apple-system, sans-serif")
          .style("font-weight", (d) =>
            d.size && d.size > maxFontSize * 0.6 ? "600" : "500"
          )
          .style("fill", (d) => d.color || colors[0])
          .style("cursor", "default")
          .style("user-select", "none")
          .style("transition", "opacity 0.3s ease, transform 0.2s ease")
          .attr("text-anchor", "middle")
          .attr(
            "transform",
            (d) => `translate(${d.x},${d.y})rotate(${d.rotate})`
          )
          .style("opacity", 0)
          .text((d) => d.text || "");

        // Animation d'apparition progressive avec requestAnimationFrame
        textElements.each(function (_, i: number) {
          const element = this as SVGTextElement;
          setTimeout(() => {
            element.style.opacity = "1";
          }, i * 20);
        });

        // Ajouter des effets de hover
        textElements
          .on("mouseenter", function () {
            const element = this as SVGTextElement;
            element.style.opacity = "1";
            element.style.filter = "brightness(1.2)";
          })
          .on("mouseleave", function () {
            const element = this as SVGTextElement;
            element.style.opacity = "0.9";
            element.style.filter = "brightness(1)";
          });

        setIsLoading(false);
      });

    layout.start();

    return () => {
      svg.selectAll("*").remove();
    };
  }, [words, dimensions, colors, isNight, rotationEnabled, minFontSize, maxFontSize]);

  // Vérifier si les données sont valides
  const hasValidData = words.length > 0;

  if (!hasValidData && !isLoading) {
    return (
      <div className="flex h-full w-full flex-col">
        {(visual.title || visual.subtitle) && (
          <div className="px-4 pb-2 pt-4">
            {visual.title && (
              <Typography variant="h6" className="text-content-primary">
                {visual.title}
              </Typography>
            )}
            {visual.subtitle && (
              <Typography variant="text-sm" className="text-content-tertiary">
                {visual.subtitle}
              </Typography>
            )}
          </div>
        )}
        <div className="flex-1 flex items-center justify-center">
          <EmptyState />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col">
      {/* Titre et sous-titre */}
      {(visual.title || visual.subtitle) && (
        <div className="px-4 pb-2 pt-4">
          {visual.title && (
            <Typography variant="h6" className="text-content-primary">
              {visual.title}
            </Typography>
          )}
          {visual.subtitle && (
            <Typography variant="text-sm" className="text-content-tertiary">
              {visual.subtitle}
            </Typography>
          )}
        </div>
      )}

      {/* Conteneur du nuage de mots */}
      <div
        ref={containerRef}
        className="relative flex-1"
        style={{
          minHeight: 200,
          height:
            visual.title || visual.subtitle ? "calc(100% - 60px)" : "100%",
        }}
      >
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loading size={32} />
          </div>
        )}
        <svg
          ref={svgRef}
          width="100%"
          height="100%"
          style={{ display: "block" }}
        />
      </div>
    </div>
  );
};

export default WordCloudWidget;
